import { useCallback, useContext } from "react";
import { AssetMovement, AssetType, BookingHistory, BookingHistoryType, CollectionDeliveryStatus, FuelType, KeyAudit, KeyAuditItem, KeyAuditItemStatus, Payment, ProductSaleItemHistory, ProductSaleItemHistoryType, VehicleAudit, VehicleAuditIssueType, VehicleAuditItem, VehicleAuditItemStatus } from "../logic/api";
import { formatCamel, formatCurrency, paymentTypeLabelMap } from "./utils";
import { SessionContext } from "./useSession";
import { ReleaseFlag, useReleaseManagement } from "./feature-management";
import { startCase } from "lodash";
import { useTranslation } from "react-i18next";
import { useDate } from "./useDate";


export const useFormatter = () => {
  const session = useContext(SessionContext);
  const isRecordAlertsReleased = useReleaseManagement(ReleaseFlag.releaseRecordAlertsInBookingHistory);
  const isUnknownMovementReleased = useReleaseManagement(ReleaseFlag.releaseUnknownMovement);
  const isShowShuttleBusPickupInBookingHistoryReleased = useReleaseManagement(ReleaseFlag.releaseShowShuttleBusPickupInBookingHistory);

  const { t } = useTranslation();
  const { toFormattedDateTimeString } = useDate();

  const convertBooleanToDisplayValue = useCallback((value: string | boolean | null | undefined | number): string => {
    if (value === true || value === "true" || value === "1" || value === 1) {
      return t('general.shortYes');
    }
    if (value === false || value === "false" || value === "0" || value === 0) {
      return t('general.shortNo');
    }
    return String(value || '');
  }, [t]);

  const generateBookingHistoryText = (historyItem: BookingHistory) => {
    const historyFieldName = historyItem.fieldAlias == null ? (historyItem.fieldName || "") : historyItem.fieldAlias;

    const fieldRef: string = historyFieldName.replace(/([a-z])([A-Z])/g, '$1 $2');

    const formattedFieldRef = fieldRef.toLowerCase().replace(/\b\w/g, (match) => match.toUpperCase());
    const fixIDCaseFieldRef = formattedFieldRef.endsWith(" Id") ? formattedFieldRef.replace(" Id", " ID") : formattedFieldRef;

    let fieldFrom = historyItem.fieldFrom;
    let fieldTo = historyItem.fieldTo;
    let additionalInfo = "";
    const fieldType = historyItem.fieldType;
    const fieldName = historyItem.fieldName;

    if (historyItem.fieldType == "date") {
      fieldFrom = toFormattedDateTimeString(new Date(fieldFrom));
      fieldTo = toFormattedDateTimeString(new Date(fieldTo));
    }
    if (historyItem.type === BookingHistoryType.undoCheckOut && historyItem.fieldAlias) {
      additionalInfo = `(${historyItem.fieldAlias})`;
    }
    if (historyItem.type === BookingHistoryType.undoCheckIn && historyItem.fieldAlias) {
      additionalInfo = `(${historyItem.fieldAlias})`;
    }
    if (historyItem.fieldName === "fuelType") {
      // Convert fuel type numbers to readable names
      const fromType = parseInt(fieldFrom);
      const toType = parseInt(fieldTo);

      if (!isNaN(fromType)) {
        fieldFrom = t(`booking.fuelTypes.${FuelType[fromType]?.toLowerCase()}`);
      }
      if (!isNaN(toType)) {
        fieldTo = t(`booking.fuelTypes.${FuelType[toType]?.toLowerCase()}`);
      }
    }
    if (historyItem.fieldType === "collectionDeliveryStatus") {
      fieldFrom = t(`booking.collectionDeliveryStatus.${fieldFrom.charAt(0).toLowerCase() + fieldFrom.slice(1)}`);
      fieldTo = t(`booking.collectionDeliveryStatus.${fieldTo.charAt(0).toLowerCase() + fieldTo.slice(1)}`);
    }
    if (historyItem.type === BookingHistoryType.updatedField && historyItem.fieldName === "preventObfuscation") {
      return t("booking.bookingHistory.deletePersonalData", {
        personName: `${historyItem.createdBy.firstName} ${historyItem.createdBy.lastName}`,
        action: fieldTo === "true" ? t(`general.disabled`) : t(`general.enabled`),
        reason: historyItem.fieldAlias || t('general.systemAction')
      });
    } else if (historyItem.fieldType == "boolean") {
      fieldFrom = convertBooleanToDisplayValue(fieldFrom);
      fieldTo = convertBooleanToDisplayValue(fieldTo);
    }
    if (fieldTo === null || fieldTo === undefined) {
      fieldTo = "";
    }
    if (fieldFrom === null || fieldTo === undefined) {
      fieldFrom = "";
    }

    if (
      historyItem.type === BookingHistoryType.shuttlePickup ||
      (historyItem.type === BookingHistoryType.updatedField &&
        historyItem.fieldName === 'pickupRequested')
    ) {
      if (!isShowShuttleBusPickupInBookingHistoryReleased) return null;

      const statusKey =
        historyItem.type === BookingHistoryType.shuttlePickup
          ? historyItem.fieldName || 'requested'
          : historyItem.fieldTo === 'true' || historyItem.fieldTo === '1'
            ? 'requested'
            : 'cancelled';

      const hasLocation =
        historyItem.type === BookingHistoryType.shuttlePickup &&
        Boolean(historyItem.fieldTo?.trim());

      return t(
        `booking.bookingHistory.shuttlePickup.${hasLocation ? statusKey : `${statusKey}NoLocation`}`,
        {
          locationName: historyItem.fieldTo,
          personName: `${historyItem.createdBy.firstName} ${historyItem.createdBy.lastName}`,
        }
      );
    }

    let translationKey;
    switch (historyItem.type) {
      case BookingHistoryType.created:
        translationKey = 'booking.bookingHistory.createdBy';
        break;
      case BookingHistoryType.preCheckedIn:
        translationKey = 'booking.bookingHistory.preCheckedIn';
        break;
      case BookingHistoryType.checkedIn:
        translationKey = 'booking.bookingHistory.checkedIn';
        break;
      case BookingHistoryType.checkedOut:
        translationKey = 'booking.bookingHistory.checkedOut';
        break;
      case BookingHistoryType.cancelled:
        translationKey = 'booking.bookingHistory.cancelledBy';
        break;
      case BookingHistoryType.updatedField:
        if (!historyItem.fieldTo) {
          translationKey = 'booking.bookingHistory.removedField';
          break;
        }

        translationKey = 'booking.bookingHistory.updatedField';
        break;
      case BookingHistoryType.event:
        translationKey = 'booking.bookingHistory.event'
        break;
      case BookingHistoryType.undoCheckOut:
        translationKey = 'booking.bookingHistory.undoCheckOut'
        break;
      case BookingHistoryType.undoCheckIn:
        translationKey = 'booking.bookingHistory.undoCheckIn';
        break;
      case BookingHistoryType.unachived:
        translationKey = 'booking.bookingHistory.unarchivedBy'
        break;
      case BookingHistoryType.uncancelled:
        translationKey = 'booking.bookingHistory.restoredBy';
        break;
      case BookingHistoryType.deleted:
        translationKey = 'booking.bookingHistory.deleted'
        break;
      case BookingHistoryType.externalReference:
        if (!historyItem.fieldTo) {
          translationKey = 'booking.bookingHistory.externalReferenceRemoved';
        }
        translationKey = 'booking.bookingHistory.externalReference';
        break;
      case BookingHistoryType.collectionDeliveryStatus:
        if (fieldTo === CollectionDeliveryStatus[CollectionDeliveryStatus.CollectionRequested]) {
          translationKey = 'booking.bookingHistory.collectionRequested';
        } else if (fieldFrom === CollectionDeliveryStatus[CollectionDeliveryStatus.CollectionRequested] && fieldTo === CollectionDeliveryStatus[CollectionDeliveryStatus.Pending]) {
          translationKey = 'booking.bookingHistory.collectionRequestedUndone';
        } else if (fieldTo === CollectionDeliveryStatus[CollectionDeliveryStatus.DeliveryRequested]) {
          translationKey = 'booking.bookingHistory.deliveryRequested';
        } else if (fieldFrom === CollectionDeliveryStatus[CollectionDeliveryStatus.DeliveryRequested] && fieldTo === CollectionDeliveryStatus[CollectionDeliveryStatus.CollectionComplete]) {
          translationKey = 'booking.bookingHistory.deliveryRequestedUndone';
        } else {
          translationKey = 'booking.bookingHistory.updatedField';
        }
        break;
      case BookingHistoryType.confirmedArrivalDate:
      case BookingHistoryType.confirmedReturnDate:
        if (!historyItem.fieldTo) {
          translationKey = 'booking.bookingHistory.removedField';
        } else {
          translationKey = 'booking.bookingHistory.updatedField';
        }
        break;
      default:
        translationKey = '';
        break;
    }

    const bookingHistoryText = isRecordAlertsReleased && historyItem.type === 7 && historyItem.fieldType === 'REPORT_ALERT'
      ? historyItem.fieldName : t(translationKey, {
        personName: `${historyItem.createdBy.firstName} ${historyItem.createdBy.lastName}`,
        additionalInfo,
        field: fixIDCaseFieldRef,
        from: fieldFrom,
        to: fieldTo,
        fieldType,
        fieldName,
      });

    return bookingHistoryText;
  }


  const generateAssetMovementText = (movement: AssetMovement) => {
    const { assetType, location, isInfo } = movement;

    const getBoxName = (movement: AssetMovement) => {
      const data = movement.additionalData as unknown as Record<string, unknown>;
      return data.name || "";
    }

    if (isInfo) {
      return t("booking.bookingHistory.assetMovements.boxMovement", { boxName: getBoxName(movement), location: location.name, firstName: movement.movedBy.firstName, lastName: movement.movedBy.lastName })
    }

    const formattedAssetType = assetType === AssetType.vehicle ? 'vehicle' : assetType === AssetType.vehicleKey ? 'key' : AssetType[assetType];

    if (isUnknownMovementReleased && !movement.movedBy && movement.recordedBy) {
      return t(`booking.bookingHistory.assetMovements.${formattedAssetType}LocationUpdated`, { locationName: location.name, recordedByFirstName: movement.recordedBy.firstName, recordedByLastName: movement.recordedBy.lastName })
    }
    else if (movement.recordedBy && movement.recordedBy.id !== movement.movedBy.id) {
      return t(`booking.bookingHistory.assetMovements.${formattedAssetType}MovementRecordedBy`, { locationName: location.name, firstName: movement.movedBy.firstName, lastName: movement.movedBy.lastName, recordedByFirstName: movement.recordedBy.firstName, recordedByLastName: movement.recordedBy.lastName })
    }
    else if (!movement.movedBy) {
      return t(`booking.bookingHistory.assetMovements.${formattedAssetType}LocationUpdatedSimple`, { locationName: location.name })
    }
    else {
      return t(`booking.bookingHistory.assetMovements.${formattedAssetType}Movement`, { locationName: location.name, firstName: movement.movedBy.firstName, lastName: movement.movedBy.lastName })
    }
  }

  const generateProductHistoryText = (saleItem: ProductSaleItemHistory) => {
    return `${formatCamel(ProductSaleItemHistoryType[saleItem.type])}: <b>${saleItem.desc}</b> by <b>${saleItem.createdBy.firstName} ${saleItem.createdBy.lastName}</b>`;
  }

  const generatePaymentHistoryText = (payment: Payment) => {
    const amount = formatCurrency(payment.amount, session);
    const paymentType = paymentTypeLabelMap.get(payment.paymentType);
    const takenBy = `${payment.addedBy.firstName} ${payment.addedBy.lastName}`;

    const additionalFields = [];
    if (payment.authCode) {
      additionalFields.push(t("payments.authCode", { paymentAuthCode: payment.authCode }));
    }

    if (payment.accountNumber) {
      additionalFields.push(t("payments.accountNumber", { paymentAccNumber: payment.accountNumber }));
    }

    const description = payment.description?.trim()
      ? `<i style={{ marginLeft: 10, color: "gray" }}>${payment.description}</i>`
      : "";

    const formattedFields = additionalFields.length > 0 ? `(${additionalFields.join()})` : '';


    return t("formatter.payment", { cost: amount, type: paymentType, fields: formattedFields, name: takenBy, desc: description });
  }

  const generateAuditHistoryText = (audit: KeyAudit | VehicleAudit) => {
    const isKeyAudit = (audit: KeyAudit | VehicleAudit): audit is KeyAudit => (Object.hasOwn(audit, "keyAuditItems"));
    const isVehicleAudit = (audit: KeyAudit | VehicleAudit): audit is VehicleAudit => (Object.hasOwn(audit, "vehicleAuditItems"));

    const getIssuesList = (issues: VehicleAuditIssueType[]) => {
      return `(<b>${issues.map(issue => startCase(VehicleAuditIssueType[issue])).join(", ")}</b>)`
    }

    const getAuditHistoryItems = (audit: KeyAudit | VehicleAudit, auditor: string) => {
      const auditType = isKeyAudit(audit) ? t("general.key") : t("booking.cards.vehicle");
      const auditItems = isKeyAudit(audit) ? audit.keyAuditItems : audit.vehicleAuditItems;
      const auditItemStatus = (item: KeyAuditItem | VehicleAuditItem) => isKeyAudit(audit)
        ? startCase(KeyAuditItemStatus[item.status])
        : startCase(VehicleAuditItemStatus[item.status]);

      if (!auditItems) {
        return [];
      }

      return auditItems.map(item => {
        const missingTranslationKey = 'formatter.auditMissing';
        const locationTranslationKey = 'formatter.auditLocation';

        if ((isKeyAudit(audit) && item.status === KeyAuditItemStatus.missing) || (isVehicleAudit(audit) && item.status === VehicleAuditItemStatus.missing)) {
          return t(missingTranslationKey, {
            auditType: auditType,
            expectedLocation: item.expectedLocation?.name || t("general.unknown"),
            auditor: auditor
          });
        }

        return t(locationTranslationKey, {
          auditType: auditType,
          parentName: audit.location?.parentName,
          name: audit.location?.name,
          auditor: auditor,
          auditItemStatus: auditItemStatus(item)
        });
      });
    }

    const getKeyAuditItemHistory = (audit: KeyAudit, auditor: string) => {
      if (!audit.keyAuditItems?.length) {
        return [];
      }

      return getAuditHistoryItems(audit, auditor);
    }

    const getVehicleAuditHistoryItems = (audit: VehicleAudit, auditor: string) => {
      const auditItemHistory = getVehicleAuditItemHistory(audit, auditor);
      const auditIssueHistory = getVehicleAuditIssueHistory(audit, auditor);
      const auditIssuePhotoHistory = getVehicleAuditIssuePhotoHistory(audit, auditor);

      return [...auditItemHistory, ...auditIssueHistory, ...auditIssuePhotoHistory];
    }

    const getVehicleAuditItemHistory = (audit: VehicleAudit, auditor: string) => {
      if (!audit.vehicleAuditItems?.length) {
        return [];
      }

      return getAuditHistoryItems(audit, auditor);
    }

    const getVehicleAuditIssueHistory = (audit: VehicleAudit, auditor: string) => {
      if (!audit.vehicleAuditIssues?.length) {
        return [];
      }

      const issues: number[] = audit.vehicleAuditIssues.map(vai => vai.issueType);

      if (!issues.length) {
        return [];
      }

      const issuesList = getIssuesList(issues);

      return [t("formatter.auditIssues", {
        length: audit.vehicleAuditIssues.length,
        count: audit.vehicleAuditIssues.length,
        name: auditor,
        audit: issuesList
      })]

    }

    const getVehicleAuditIssuePhotoHistory = (audit: VehicleAudit, auditor: string) => {
      if (!audit.vehicleAuditIssuePhotos?.length) {
        return [];
      }

      return [t("formatter.photoAdded", {
        length: audit.vehicleAuditIssuePhotos?.length,
        count: audit.vehicleAuditIssuePhotos?.length,
        name: auditor
      })]
    }

    const getAuditItems = (audit: KeyAudit | VehicleAudit) => {
      const auditor = `${audit.createdBy.firstName} ${audit.createdBy.lastName}`

      if (isKeyAudit(audit)) {
        return getKeyAuditItemHistory(audit, auditor);
      }

      if (isVehicleAudit(audit)) {
        return getVehicleAuditHistoryItems(audit, auditor);
      }

      return [];
    }

    return getAuditItems(audit);
  }

  return {
    bookingHistoryFormatter: generateBookingHistoryText,
    assetMovementFormatter: generateAssetMovementText,
    productHistoryFormatter: generateProductHistoryText,
    paymentHistoryFormatter: generatePaymentHistoryText,
    auditHistoryFormatter: generateAuditHistoryText
  }
};


